// Report Requests — HTTP transport for B3's issue + review surface on the
// loan's Financial Reporting tab. Mirrors
// PhoenixCapital.Core/DTOs/ReportRequests/ReportRequestDtos.cs and the routes
// on PhoenixCapital.Api/Controllers/ReportRequests/ReportRequestsController.cs.
//
// Replaces the retired borrowerReportingApi (the catalog-driven receipt
// ledger). The shape difference that matters: nothing here is generated by a
// read. A request exists only because a human issued it.
//
// Enums cross the wire as STRINGS (Program.cs registers JsonStringEnumConverter),
// so `requestType`/`kind`/`applicability` are the C# member names verbatim.
// Hooks live in @/hooks/portfolioAdmin/useReportRequests; query keys in
// reportRequestKeys.

import { api } from "../client";

// --- Vocabulary --------------------------------------------------------------

/** The four types B3 may issue. There is deliberately no routine monthly
 *  type — monthly collection happens only through DscrWatchMonthly, issued
 *  by hand while the loan is on watch. */
export type ReportRequestType =
  | "Quarterly"
  | "Annual"
  | "DscrWatchMonthly"
  | "DefaultWorkout";

/** Display order for the issue dialog's type picker. */
export const REPORT_REQUEST_TYPES: ReportRequestType[] = [
  "Quarterly",
  "Annual",
  "DscrWatchMonthly",
  "DefaultWorkout",
];

export const REPORT_REQUEST_TYPE_LABEL: Record<ReportRequestType, string> = {
  Quarterly: "Quarterly",
  Annual: "Annual",
  DscrWatchMonthly: "Monthly DSCR-Watch",
  DefaultWorkout: "Default / Workout",
};

/** How the borrower satisfies an item. */
export type ReportRequestItemKind =
  | "Form"
  | "Upload"
  | "FormOrUpload"
  | "FormWithSignature";

export const REPORT_REQUEST_ITEM_KINDS: ReportRequestItemKind[] = [
  "Form",
  "Upload",
  "FormOrUpload",
  "FormWithSignature",
];

export const REPORT_REQUEST_ITEM_KIND_LABEL: Record<ReportRequestItemKind, string> = {
  Form: "Fill in a form",
  Upload: "Attach a file",
  FormOrUpload: "Form or attachment",
  FormWithSignature: "Form, signed",
};

/** Always / once per guarantor / issued by default but commonly unticked. */
export type ReportRequestItemApplicability =
  | "Always"
  | "PerGuarantor"
  | "IfApplicable";

// Statuses are widened to `string` deliberately: an unrecognised server value
// must render as itself rather than crash a lookup.
export type ReportRequestStatus =
  | "issued"
  | "in-progress"
  | "under-review"
  | "complete"
  | "reported"
  | "cancelled"
  | string;

export const REPORT_REQUEST_STATUS_LABEL: Record<string, string> = {
  issued: "Sent to borrower",
  "in-progress": "Borrower working on it",
  "under-review": "Waiting for your review",
  complete: "Complete",
  reported: "Reported",
  cancelled: "Cancelled",
};

export type ReportRequestItemStatus =
  | "pending"
  | "submitted"
  | "under-review"
  | "approved"
  | "returned"
  | string;

export const REPORT_REQUEST_ITEM_STATUS_LABEL: Record<string, string> = {
  pending: "Not yet provided",
  submitted: "Submitted",
  "under-review": "Under review",
  approved: "Approved & filed",
  returned: "Returned to borrower",
};

/** Still owed by the borrower — mirrors ReportRequestItemStatuses.Outstanding. */
export const OUTSTANDING_ITEM_STATUSES = ["pending", "returned"];

/** Terminal request states: no further issue/review action is offered. */
export const TERMINAL_REQUEST_STATUSES = ["cancelled", "reported"];

// --- Shapes ------------------------------------------------------------------

export interface ReportRequestItemDto {
  id: string;
  itemCode: string;
  displayName: string;
  kind: ReportRequestItemKind;
  displayOrder: number;
  status: ReportRequestItemStatus;
  collectionItemId: string | null;
  guarantorPartyId: string | null;
  guarantorLabel: string | null;
  reviewerNote: string | null;
  /**
   * When the borrower's current submission arrived. Server-resolved from the
   * staged submission the review actions themselves act on — NOT from
   * LoanReportRequestItem.SubmittedAt, a column nothing writes. Null exactly
   * when there is no live staged submission.
   */
  submittedAt: string | null;
  filedDocumentId: string | null;
  approvedAt: string | null;
  approvedByUserName: string | null;
  /**
   * The current staged submission is still awaiting review. This is the ACTUAL
   * precondition the server enforces — File / Approve / Return all refuse a
   * staged row that is not AwaitingReview — so gate the review actions on this
   * rather than on a timestamp, which stays set after the row has been
   * reviewed.
   */
  awaitingReview: boolean;
  /**
   * The staged submission a review action would act on. Carried so "nothing was
   * ever submitted" can be told apart from "the last submission has already
   * been reviewed" — different states with different things to say.
   */
  stagedSubmissionId: string | null;
}

export interface ReportRequestDto {
  id: string;
  loanId: string;
  requestType: ReportRequestType;
  requestTypeLabel: string;
  /** Wall-calendar values — render with formatCalendarDate, never the
   *  tz-aware formatter. */
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
  dueDate: string;
  status: ReportRequestStatus;
  collectionPackageId: string | null;
  defaultCaseId: string | null;
  /** A genuine instant — tz-aware formatting is correct here. */
  issuedAt: string;
  issuedByUserName: string | null;
  completedAt: string | null;
  completeWithExceptions: boolean;
  exceptionsNote: string | null;
  generatedReportSubmissionId: string | null;
  filedDocumentId: string | null;
  filedAt: string | null;
  itemCount: number;
  approvedCount: number;
  outstandingCount: number;
  items: ReportRequestItemDto[];
  /** The generated report to open in the editor. Null until it is generated. */
  generatedReportId: string | null;
  /**
   * Server-computed: every item approved, or the request explicitly marked
   * complete-with-exceptions, and nothing filed yet. Read it rather than
   * re-deriving the rule here — a button that offers what the endpoint refuses
   * is exactly what sharing the predicate prevents.
   */
  canGenerateReport: boolean;
}

export interface ReportRequestIssueItemOptionDto {
  itemCode: string;
  displayName: string;
  kind: ReportRequestItemKind;
  applicability: ReportRequestItemApplicability;
  guidance: string;
  selectedByDefault: boolean;
  /** How many rows this fans out to. 0 for a PerGuarantor item on a loan with
   *  no guarantors — the server generates nothing at all in that case. */
  fanOutCount: number;
}

export interface ReportRequestIssueOptionsDto {
  requestType: ReportRequestType;
  requestTypeLabel: string;
  suggestedPeriodStart: string;
  suggestedPeriodEnd: string;
  suggestedPeriodLabel: string;
  /**
   * Null exactly when the due date is UNCOMPUTABLE — Annual on a loan with no
   * recorded tax-completion date, since that date is the whole input. This is
   * NOT the same question as `canIssue`: a type can be blocked while its due
   * date computes perfectly well (DSCR-Watch off watch, Default/Workout with no
   * open case). The two coincided while Annual was the only enforced
   * precondition, which is how the other three came to be advisory.
   */
  suggestedDueDate: string | null;
  /**
   * Server-computed and server-ENFORCED — the issue endpoint rejects the same
   * cases with the same sentence (see ReportRequestIssueGate). Read it rather
   * than re-deriving the rule here.
   */
  canIssue: boolean;
  /** Why not, phrased for the person who clicked. Null when `canIssue`. */
  blockedReason: string | null;
  items: ReportRequestIssueItemOptionDto[];
}

/**
 * Decision 10's mitigation. DSCR-Watch requests are issued MANUALLY each
 * month, so `currentMonthIssued` has to be shown rather than inferred from an
 * absence — a missed month looks identical to a month nobody has reached yet.
 */
export interface DscrWatchStateDto {
  isOnDscrWatch: boolean;
  startedAt: string | null;
  startedByUserName: string | null;
  clearedAt: string | null;
  currentMonthIssued: boolean;
  currentMonthLabel: string;
}

export interface LoanReportRequestOverviewDto {
  loanId: string;
  dscrWatch: DscrWatchStateDto;
  taxesCompletedAt: string | null;
  taxesCompletedRecordedByUserName: string | null;
  requests: ReportRequestDto[];
}

// --- Request bodies ----------------------------------------------------------

export interface IssueReportRequestRequest {
  requestType: ReportRequestType;
  periodStart: string;
  periodEnd: string;
  periodLabel: string | null;
  dueDate: string;
  /** The SELECTED subset of item codes, not the whole default list. */
  itemCodes: string[];
  adHocItemName: string | null;
  adHocItemKind: ReportRequestItemKind | null;
  adHocItemGuidance: string | null;
}

export interface SetDscrWatchRequest {
  on: boolean;
  note: string | null;
}

export interface ApproveReportRequestItemRequest {
  folderId: string;
  reviewerNote: string | null;
}

// --- Transport ---------------------------------------------------------------

const BASE = "/portfolio-admin";

export const reportRequestsApi = {
  getOverview: (loanId: string) =>
    api.get<LoanReportRequestOverviewDto>(`${BASE}/loans/${loanId}/report-requests`),

  getIssueOptions: (loanId: string, type: ReportRequestType) =>
    api.get<ReportRequestIssueOptionsDto>(
      `${BASE}/loans/${loanId}/report-requests/issue-options`,
      { type },
    ),

  get: (requestId: string) =>
    api.get<ReportRequestDto>(`${BASE}/report-requests/${requestId}`),

  issue: (loanId: string, body: IssueReportRequestRequest) =>
    api.post<ReportRequestDto>(`${BASE}/loans/${loanId}/report-requests`, body),

  completeWithExceptions: (requestId: string, note: string) =>
    api.post<ReportRequestDto>(
      `${BASE}/report-requests/${requestId}/complete-with-exceptions`,
      { note },
    ),

  cancel: (requestId: string, note: string) =>
    api.post<ReportRequestDto>(`${BASE}/report-requests/${requestId}/cancel`, { note }),

  approveItem: (itemId: string, body: ApproveReportRequestItemRequest) =>
    api.post<ReportRequestItemDto>(`${BASE}/report-request-items/${itemId}/approve`, body),

  returnItem: (itemId: string, note: string) =>
    api.post<ReportRequestItemDto>(`${BASE}/report-request-items/${itemId}/return`, { note }),

  /** Writes the AI summary. Idempotent server-side — a request that already
   *  has a report is returned unchanged rather than generating a second one. */
  generateReport: (requestId: string) =>
    api.post<ReportRequestDto>(`${BASE}/report-requests/${requestId}/generate-report`, {}),

  /** Renders B3's edited draft and files it into the chosen loan folder. */
  finaliseReport: (requestId: string, folderId: string) =>
    api.post<ReportRequestDto>(`${BASE}/report-requests/${requestId}/finalise-report`, {
      folderId,
    }),

  setDscrWatch: (loanId: string, body: SetDscrWatchRequest) =>
    api.post<DscrWatchStateDto>(`${BASE}/loans/${loanId}/report-requests/dscr-watch`, body),

  /** Returns the stored date (or null when cleared). */
  recordTaxesCompleted: (loanId: string, taxesCompletedAt: string | null) =>
    api.post<string | null>(`${BASE}/loans/${loanId}/report-requests/taxes-completed`, {
      taxesCompletedAt,
    }),
};
